'use client';

import { startNavigationProgress, startTopProgress } from '@/shared/lib/topProgress';
import { useRouter } from 'next/navigation';
import { type TransitionStartFunction, useCallback, useEffect, useMemo, useTransition } from 'react';

export const useTopProgress = (isActive: boolean): void => {
  useEffect(() => {
    if (!isActive) return;
    return startTopProgress();
  }, [isActive]);
};

export const useProgressTransition = (): [boolean, TransitionStartFunction] => {
  const [isPending, startTransition] = useTransition();
  useTopProgress(isPending);
  return [isPending, startTransition];
};

type ProgressRouter = ReturnType<typeof useRouter> & { isNavigating: boolean };

export const useProgressRouter = (): ProgressRouter => {
  const router = useRouter();
  const [isNavigating, startTransition] = useProgressTransition();

  const push = useCallback<ProgressRouter['push']>((...args) => startTransition(() => router.push(...args)), [router, startTransition]);
  const replace = useCallback<ProgressRouter['replace']>((...args) => startTransition(() => router.replace(...args)), [router, startTransition]);
  const refresh = useCallback(() => startTransition(() => router.refresh()), [router, startTransition]);
  const back = useCallback(() => {
    startNavigationProgress();
    router.back();
  }, [router]);

  return useMemo(() => ({ ...router, push, replace, refresh, back, isNavigating }), [router, push, replace, refresh, back, isNavigating]);
};
